import React, { Component } from 'react';
import isMobileAppRuntime from '../../utils/isMobileAppRuntime';

const MOBILE_ERROR_STORAGE_KEY = 'flc.mobile.lastReactError';

const getErrorMessage = (error) => {
  if (!error) return 'Unknown frontend error';
  return error.message || String(error);
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (!isMobileAppRuntime()) {
      // Preserve the original desktop ErrorBoundary behavior.
      console.log(error, errorInfo);
      return;
    }

    const diagnostic = this.createDiagnostic(error, errorInfo);

    this.setState({ error, errorInfo });
    console.error('[FLC mobile frontend error]', diagnostic);

    try {
      window.localStorage.setItem(
        MOBILE_ERROR_STORAGE_KEY,
        JSON.stringify(diagnostic)
      );
    } catch (storageError) {
      console.error('[FLC mobile] Unable to persist React diagnostic', storageError);
    }
  }

  createDiagnostic = (error = this.state.error, errorInfo = this.state.errorInfo) => ({
    timestamp: new Date().toISOString(),
    message: getErrorMessage(error),
    stack: error?.stack || '',
    componentStack: errorInfo?.componentStack || '',
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  });

  copyDiagnostic = async () => {
    const text = JSON.stringify(this.createDiagnostic(), null, 2);

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      this.setState({ copied: true });
    } catch (error) {
      console.error('[FLC mobile] Unable to copy diagnostic', error);
    }
  };

  reloadApp = () => {
    window.location.reload();
  };

  renderMobileError() {
    const { error, errorInfo, copied } = this.state;
    const message = getErrorMessage(error);
    const details = [error?.stack, errorInfo?.componentStack]
      .filter(Boolean)
      .join('\n\n');

    return (
      <main
        role="alert"
        style={{
          minHeight: '100vh',
          boxSizing: 'border-box',
          padding: 'max(24px, env(safe-area-inset-top)) max(24px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom)) max(24px, env(safe-area-inset-left))',
          background: '#fff',
          color: '#222',
          fontFamily: 'Arial, sans-serif',
          overflow: 'auto',
        }}
      >
        <h1 style={{ margin: '0 0 12px', fontSize: '28px' }}>Mobile UI error</h1>
        <p style={{ margin: '0 0 16px', fontSize: '18px', lineHeight: 1.4 }}>
          {message}
        </p>
        <p style={{ margin: '0 0 20px', fontSize: '15px', lineHeight: 1.4 }}>
          The error is stored locally under <code>{MOBILE_ERROR_STORAGE_KEY}</code>.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button type="button" onClick={this.copyDiagnostic} style={{ minHeight: '48px', padding: '10px 18px', fontSize: '16px' }}>
            {copied ? 'Diagnostics copied' : 'Copy diagnostics'}
          </button>
          <button type="button" onClick={this.reloadApp} style={{ minHeight: '48px', padding: '10px 18px', fontSize: '16px' }}>
            Reload app
          </button>
        </div>
        {details && (
          <details open style={{ fontSize: '13px', lineHeight: 1.45 }}>
            <summary style={{ cursor: 'pointer', fontSize: '16px', marginBottom: '10px' }}>Technical details</summary>
            <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', background: '#f4f4f4', padding: '12px' }}>
              {details}
            </pre>
          </details>
        )}
      </main>
    );
  }

  render() {
    if (this.state.hasError) {
      if (isMobileAppRuntime()) return this.renderMobileError();
      return <h2 style={{ textAlign: 'center' }}>Something went wrong.</h2>;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
